/**
 * The desk.
 *
 * A 900px working area on the left and the map filling the rest — the three things a
 * dispatcher does at once: typing one call, watching the last one get covered, and glancing
 * at where the cars are.
 *
 * Inside the working area, two columns that know nothing of each other's height: a 340px
 * one for the telephone, the order form, the callers on hold, the call-backs and the
 * bookings for later; a wider one for the queue, the open order and the people waiting for
 * a car.
 */

import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { CallPop } from '../calls/CallPop';
import { MessagesCard } from '../messages/MessagesCard';
import { MissedCalls } from '../calls/MissedCalls';
import { OrderEntry } from '../orders/OrderEntry';
import { OrderDetail } from '../orders/OrderDetail';
import { PreBookings } from '../orders/PreBookings';
import { QueueCard } from '../orders/QueueCard';
import { useQueue } from '../orders/useQueue';
import { useDispatch } from '../shell/dispatchContext';
import { HeldCalls } from '../telephony/HeldCalls';
import { ParkedCalls } from '../telephony/ParkedCalls';
import { Softphone } from '../telephony/Softphone';
import { Card, EmptyState, Icon } from '../ui';
import { DEFAULT_WIDTH, useWorkingWidth } from './useWorkingWidth';

/**
 * Loaded on demand, because MapLibre is most of a megabyte.
 *
 * Bundled with the rest, it would be downloaded and parsed before a dispatcher could type
 * their password — paying for the map on the one screen that cannot show one.
 */
const DriverMap = lazy(async () => ({ default: (await import('../map/DriverMap')).DriverMap }));

export function DeskView() {
  const { t } = useTranslation();
  const { fromCall, takeCall, orderOnCall, onCreated, lastTaken } = useDispatch();
  const { orders, loading, selected, select, close, cancelled, refresh, reload } = useQueue();
  const { width, startDrag, reset, nudge } = useWorkingWidth();

  return (
    <div
      /* No column gap: the divider is the gap, twelve pixels like every other one on the
         screen, so the map sits as close to the queue as the queue sits to the telephone. */
      className="grid grid-rows-[minmax(0,1fr)] gap-x-0 gap-y-3 p-3 flex-1 min-h-0"
      style={{ gridTemplateColumns: `${width}px 12px minmax(0,1fr)` }}
    >
      <div className="grid grid-cols-[340px_minmax(0,1fr)] gap-3 content-start items-start min-h-0 overflow-y-auto">
        <div className="flex flex-col gap-3 min-w-0">
          {/* The telephone above the pop, and the pop above the form, in the order a call
              goes through them: it rings, you see who it is, you take the order. */}
          <Softphone orderOnCall={orderOnCall} caller={fromCall?.name} />

          <CallPop onTake={takeCall} />

          {/* Keyed by the call, so taking one resets the form to that caller rather than
              writing over whatever a half-finished order had in it. */}
          <OrderEntry
            key={fromCall?.callId ?? 'blank'}
            onCreated={onCreated}
            prefill={fromCall}
            lastTaken={lastTaken}
          />

          {/* The callers on hold on the PBX, under the form: the dispatcher who parked
              somebody to take the next call finds them where they were working, not across
              the screen. Empty most of the time, and gone when it is. */}
          <ParkedCalls />

          {/* The people nobody has got to yet, in the same column and independent of
              whatever the queue beside them is showing. These used to be a second grid
              row under both columns, and a grid row starts below the tallest cell of the
              row above it — so opening an order pushed the call-backs to the bottom of
              the order's detail, out of the place the dispatcher had just looked. */}
          <MissedCalls />
          <PreBookings />
        </div>

        <div className="flex flex-col gap-3">
          <QueueCard
            orders={orders}
            loading={loading}
            selectedId={selected?.id ?? null}
            onOpen={(orderId) => void select(orderId)}
          />

          {selected !== null && (
            <OrderDetail
              order={selected}
              onClose={close}
              onChanged={() => void reload()}
              onCancelled={cancelled}
              /* Whether the automation still has it is dispatch's state rather than the
                 order's, so it comes from the queue this view is already holding. The queue
                 is what the button reads its pressed state back from, which is why toggling
                 refreshes that rather than the order. */
              automatic={orders.find((o) => o.orderId === selected.id)?.auto.automatic}
              onAutomaticChanged={() => void refresh()}
            />
          )}

          {/* The people waiting for a car, under the queue they are waiting on. Usually
              empty, and gone when it is. */}
          <HeldCalls />

          {/* The drivers' words, under the queue they are about. Gone on a firm that has
              switched messages off. */}
          <MessagesCard />
        </div>
      </div>

      {/* The divider: drag it to trade map for queue. The grip in its middle is the one
          mark that says so — quiet in `ink-3`, darker under the pointer. Double-click puts
          the desk back to its shipped width; the arrow keys move it for a keyboard. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('layout.divider')}
        aria-valuenow={width}
        aria-valuemin={DEFAULT_WIDTH}
        tabIndex={0}
        onPointerDown={startDrag}
        onDoubleClick={reset}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') nudge(-24);
          else if (event.key === 'ArrowRight') nudge(24);
          else return;
          event.preventDefault();
        }}
        className="cursor-col-resize flex items-center justify-center text-ink-3 hover:text-ink focus-visible:text-ink outline-none touch-none"
      >
        <Icon name="grip" small />
      </div>

      <Suspense
        fallback={
          <Card className="flex flex-col min-h-0">
            <EmptyState>{t('app.loading')}</EmptyState>
          </Card>
        }
      >
        <DriverMap selectedOrder={selected} />
      </Suspense>
    </div>
  );
}
